import React from 'react';
import PictureImage from '../Common/PictureImage';
import './ShopifyFulfilment.css';
import ShopifyFeatures from './ShopifyFeatures';
import ShopifyIntegration from './ShopifyIntegration';
import ShopifyBenefits from './ShopifyBenefits';
import ShopifyProcessSections from './ShopifyProcessSections';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import { Link } from 'react-router-dom';
import ContactForm from '../Common/ContactForm';
import Canonical from '../../seo/Canonical';
import ComparisonTable from '../Common/ComparisonTable';
import RelatedLinks from '../Common/RelatedLinks';
import PageLastUpdated from '../Common/PageLastUpdated';
import FAQSection from '../FAQSection';
import {
  COMPANY_INTEGRATIONS_COUNT,
  COMPANY_PICKING_ACCURACY,
  COMPANY_PICK_PACK_FROM,
  COMPANY_SHIPPING_FROM,
} from '../../seo/companyFacts';
import '../Homepage/FAQ.css';

const ShopifyFulfilment: React.FC = () => {
  const shopifyFaqs = [
    {
      question: 'How does Shopify integration work?',
      answer:
        'We connect your Shopify store to our WMS so orders import automatically and inventory updates sync back in real time. No manual file uploads are required.',
    },
    {
      question: 'Do you support real-time order sync?',
      answer:
        'Yes. Orders, tracking and stock levels are synced in near real time, helping prevent overselling and keeping customers informed.',
    },
    {
      question: 'Do you offer branded packaging?',
      answer:
        'Yes. We can store and apply branded boxes, mailers and inserts per your SOPs to keep your unboxing consistent with your brand.',
    },
    {
      question: 'What are your dispatch times?',
      answer:
        'Orders received before the agreed cut-off (often 1–3pm, with same-day processing before 6 PM where agreed) are dispatched the same day. We track on-time performance and share reports.',
    },
    {
      question: 'How are returns handled?',
      answer:
        'Returns are scanned and graded within 24 hours. Sellable items are restocked the same day; quarantined items follow your rules for rework or disposal.',
    },
    {
      question: 'Which couriers do you use?',
      answer:
        'We use Royal Mail, DPD, DHL, Evri and UPS. Labels are selected by SLA, weight and destination to balance delivery speed and cost.',
    },
  ];

  return (
    <div className="shopify-fulfilment-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <section className="sh-section">
        <div className="sh-bg">
          <PictureImage
            src="/Images/shopify-fulfilment-hero.webp"
            alt="Shopify fulfilment UK warehouse — pick, pack and dispatch for ecommerce brands"
            className="sh-bg-img"
            isHero
          />
          <div className="sh-fade-overlay" />
        </div>

        <div className="sh-content">
          <div className="sh-left">
            <h1 className="sh-main-title">
              <span style={{ color: '#28479e' }}>Shopify Fulfilment UK:</span>{' '}
              <span style={{ color: '#e07b20' }}>Fast, Scalable Ecommerce Fulfilment</span>
            </h1>

            <PageLastUpdated path="/shopify-fulfillment-services" className="page-last-updated--hero" />

            <div className="sh-desc">
              <p>
                Shopify fulfilment UK means your store orders sync to a warehouse that picks, packs and ships without
                you holding stock at home. Pick Pack Pro runs that from our{' '}
                <Link to="/our-fulfilment-center" className="shopify-inline-link">
                  UK fulfilment centre
                </Link>{' '}
                with pick and pack from {COMPANY_PICK_PACK_FROM}, tracked shipping from {COMPANY_SHIPPING_FROM}, a{' '}
                {COMPANY_PICKING_ACCURACY} accuracy target and {COMPANY_INTEGRATIONS_COUNT} channel integrations.
              </p>
              <p>
                Compare{' '}
                <Link to="/pricing-fulfilment-services" className="shopify-inline-link">
                  fulfilment pricing UK
                </Link>
                ,{' '}
                <Link to="/multi-channel-fulfillment-services" className="shopify-inline-link">
                  multi-channel fulfilment
                </Link>
                ,{' '}
                <Link to="/tiktok-shop-fulfillment-services" className="shopify-inline-link">
                  TikTok Shop fulfilment
                </Link>
                , or{' '}
                <Link to="/amazon-fba-prep-services" className="shopify-inline-link">
                  Amazon FBA prep
                </Link>{' '}
                from the same centre.
              </p>
            </div>

            <ul className="sh-features">
              {[
                'Picking Fee from £0.99',
                'Next Day Shipping from £1.99',
                '99.9% Order Accuracy',
                'Dedicated Account Manager',
                'Price Match Promise',
              ].map((item, i) => (
                <li key={i} className="sh-feature-item">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                    <path
                      d="M3 9.5l4 4L15 5"
                      stroke="#e07b20"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="sh-cta-wrapper">
              <Link to="/contact-fulfillment-services" className="sh-cta-btn">
                Get a Custom Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section style={{ maxWidth: '800px', margin: '4rem auto', padding: '0 1.5rem' }}>
        <div style={{ background: '#ffffff', padding: '2.5rem', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '1.75rem', color: '#28479e', fontWeight: 'bold', textAlign: 'center' }}>
            Tell us about your Shopify store
          </h2>
          <p style={{ textAlign: 'center', color: '#4a5568', marginBottom: '2rem' }}>
            Fill in the details below and our team will get back to you with a custom fulfilment proposal.
          </p>
          <ContactForm source="shopify-fulfillment-services" isShowShopify={true} />
        </div>
      </section>

      <ComparisonTable
        title="Shopify fulfilment: Pick Pack Pro vs doing it yourself"
        subtitle={
          <>
            Compare Shopify fulfilment in the UK with in-house operations and discover how our{' '}
            <Link to="/our-fulfilment-center" className="shopify-inline-link">
              fulfilment centre in the UK
            </Link>{' '}
            simplifies growth, accuracy, and order processing. See our{' '}
            <Link to="/pricing-fulfilment-services" className="shopify-inline-link">
              fulfilment pricing
            </Link>{' '}
            for transparent rates.
          </>
        }
        leftLabel="Pick Pack Pro"
        rightLabel="In‑house"
        rows={[
          {
            feature: 'Dispatch speed',
            pickPackPro: 'Same‑day dispatch within agreed cut‑offs (often before 6 PM)',
            alternative: 'Often slows during peaks, staff gaps or stock counts',
          },
          {
            feature: 'Accuracy',
            pickPackPro: 'Scanner-verified pick/pack with defined quality control processes',
            alternative: 'Manual processes can drift as volume grows',
          },
          {
            feature: 'Integration',
            pickPackPro: 'Live Shopify order synchronisation with tracking pushed back automatically',
            alternative: 'Apps, spreadsheets, or manual label workflows',
          },
          {
            feature: 'Packaging',
            pickPackPro: 'Custom branded packaging, marketing inserts and kitting services',
            alternative: 'Hard to standardise with temp staff',
          },
          {
            feature: 'Courier mix',
            pickPackPro: 'Rate/SLA routing across RM, DPD, DHL, Evri, UPS',
            alternative: 'Limited lanes or higher unit rates',
          },
          {
            feature: 'Time & focus',
            pickPackPro: 'Your team focuses on marketing and product',
            alternative: 'Leadership time consumed by warehouse ops',
          },
        ]}
      />

      <ShopifyFeatures />
      <ShopifyIntegration />
      <ShopifyBenefits />
      <ShopifyProcessSections />

      <section style={{ maxWidth: '1200px', margin: '4rem auto 0', padding: '0 1.5rem' }}>
        <div className="home-hero-aeo-summary" style={{ background: 'rgba(245, 128, 48, 0.08)', borderLeft: '4px solid #e07b20', padding: '1.5rem', borderRadius: '4px', fontSize: '1.05rem', lineHeight: '1.6', color: '#111827' }}>
          <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#28479e', fontSize: '1.15rem' }}>Executive Summary</strong>
          Pick Pack Pro delivers specialized Shopify Fulfilment Services in the UK for growing DTC brands and multichannel ecommerce businesses. Integrating directly with the <a href="https://www.shopify.com/uk/fulfillment" target="_blank" rel="noopener noreferrer nofollow" style={{ color: '#28479e', textDecoration: 'underline' }}>Shopify platform</a>, our Milton Keynes facility offers same-day dispatch and 99.9% picking accuracy. Based on our experience shipping over 2 million DTC orders, outsourcing your Shopify logistics to a reliable 3PL eliminates bottlenecks and allows you to focus purely on scaling your storefront.
        </div>
      </section>

      <FAQSection
        headingId="shopify-faq"
        className="faqs-section"
        title="Frequently Asked Questions"
        description="Find quick answers about Shopify fulfilment UK, integrations, dispatch timelines, returns management, inventory visibility, and onboarding."
        faqs={[
          ...shopifyFaqs,
          {
            question: 'How quickly can we go live after onboarding?',
            answer: 'Once SKUs are mapped and stock arrives, many brands go live within a few working days.',
          },
          {
            question: 'Can you sync inventory across Shopify and TikTok Shop?',
            answer:
              'Yes. We can unify stock for Shopify, TikTok Shop, Amazon and eBay with channel-specific rules through our multi-channel fulfilment setup.',
          },
          {
            question: 'Do you provide performance reporting?',
            answer:
              'Yes—cut-off compliance, on-time rate, pick accuracy, returns reasons and courier performance, delivered on an agreed cadence via real-time dashboards.',
          },
        ]}
      />

      <RelatedLinks
        serviceLinks={[
          { to: '/multi-channel-fulfillment-services', label: 'Multi-channel fulfilment services' },
          { to: '/return-handling-services', label: 'Returns handling services UK' },
          { to: '/tiktok-shop-fulfillment-services', label: 'TikTok Shop fulfilment services' },
          { to: '/amazon-fba-prep-services', label: 'Amazon FBA prep services' },
          { to: '/wms-integrations', label: 'WMS integrations' },
        ]}
        blogLink={{
          to: '/blog/multi-channel-fulfilment-uk',
          label: 'Multi-channel fulfilment UK guide',
        }}
      />

      <Newsletter />
      <Footer />
    </div>
  );
};

export default ShopifyFulfilment;
